import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { shellKeys } from '@app/api/hooks/shell';
import { get, post, withPath } from '@app/api/typed';
import type { ApiData } from '@app/api/typed';
import { contractsKeys } from '../Contracts/Contracts.api';
import type { AccessControlState, CandidateRequest } from './AccessControlModel';

export type CandidateResult = ApiData<'/api/v1/identities/{id}/access-control/candidate', 'post'>;

export const accessKeys = {
  estado: (id: string) => ['access-control', id] as const,
};

/**
 * Estado editável de T14 (FR-071…FR-076). Identidade sem contrato ativo responde
 * `409 sem_contrato_ativo` — a tela mostra "aprove a primeira release antes".
 */
export function useAccessControl(id: string | null): UseQueryResult<AccessControlState, ApiError> {
  return useQuery<AccessControlState, ApiError>({
    queryKey: accessKeys.estado(id ?? ''),
    queryFn: () =>
      get(
        withPath('/api/v1/identities/{id}/access-control', {
          id: id ?? '',
        }) as '/api/v1/identities/{id}/access-control',
      ),
    enabled: Boolean(id),
    retry: false,
  });
}

export interface CandidateVars {
  id: string;
  body: CandidateRequest;
}

/**
 * **Salvar → enviar para aprovação** (FR-078). Não altera o contrato vigente:
 * o executor calcula o diff e o produto cria a pendência (origem
 * `access-control`). Sem diferença nenhuma não há pendência (FR-077).
 *
 * **Uma exceção, desde 29/08:** quando o pedido só *tira* autoridade e a
 * identidade não roda em produção, a plataforma fecha a pendência na mesma
 * chamada e o contrato v+1 nasce (`approval.status === 'auto'`). A rota continua
 * sem aplicar nada por conta própria — quem aplica é a aprovação —, mas aí o
 * vigente **mudou**, e por isso esse caso invalida também o contrato e a lista de
 * identidades. Sem isso T13 e T14 seguiriam mostrando o v anterior até um F5.
 */
export function useSubmitCandidate(): UseMutationResult<CandidateResult, ApiError, CandidateVars> {
  const queryClient = useQueryClient();
  return useMutation<CandidateResult, ApiError, CandidateVars>({
    mutationFn: ({ id, body }) =>
      post(
        withPath('/api/v1/identities/{id}/access-control/candidate', {
          id,
        }) as '/api/v1/identities/{id}/access-control/candidate',
        { body },
      ),
    onSuccess: (resultado, { id }) => {
      // A fila de aprovações e o contador do menu mudam.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      void queryClient.invalidateQueries({ queryKey: accessKeys.estado(id) });
      void queryClient.invalidateQueries({ queryKey: shellKeys.shell });

      // Só o desfecho automático mexe no vigente. Invalidar sempre custaria duas
      // buscas a cada Salvar para não mudar nada em 99 % dos casos.
      if (resultado.approval?.status !== 'auto') return;
      void queryClient.invalidateQueries({ queryKey: contractsKeys.vigente(id) });
      void queryClient.invalidateQueries({ queryKey: contractsKeys.historico(id) });
      void queryClient.invalidateQueries({ queryKey: ['identities'] });
    },
  });
}
